import { ref, computed } from 'vue'

export interface PageInfo {
    hasNextPage: boolean
    hasPreviousPage: boolean
    startCursor?: string | null
    endCursor?: string | null
}

export function useCursorPagination(opts: { defaultPageSize?: number } = {}) {
    const pageSize = ref(opts.defaultPageSize ?? 50)
    const cursor = ref<string | null>(null)
    const cursorHistory = ref<(string | null)[]>([null])
    const pageInfo = ref<PageInfo>({ hasNextPage: false, hasPreviousPage: false })
    const totalCount = ref<number | null>(null)
    const lastPageItemCount = ref(0)

    const currentPageIndex = computed(() => cursorHistory.value.length - 1)
    // hasPrev derived from local navigation history, NOT from backend pageInfo.
    // Relay-style cursor pagination often reports hasPreviousPage: false even
    // after navigating forward, so we trust our own back-stack.
    const canGoPrev = computed(() => cursorHistory.value.length > 1)
    const canGoNext = computed(() => pageInfo.value.hasNextPage)

    // 1-based range of items shown on current page (e.g. "21–40").
    const rangeStart = computed(() =>
        lastPageItemCount.value > 0 ? currentPageIndex.value * pageSize.value + 1 : 0
    )
    const rangeEnd = computed(() =>
        lastPageItemCount.value > 0
            ? currentPageIndex.value * pageSize.value + lastPageItemCount.value
            : 0
    )

    function nextPage() {
        if (pageInfo.value.hasNextPage && pageInfo.value.endCursor) {
            cursor.value = pageInfo.value.endCursor
            cursorHistory.value.push(cursor.value)
        }
    }

    function prevPage() {
        if (cursorHistory.value.length > 1) {
            cursorHistory.value.pop()
            cursor.value = cursorHistory.value[cursorHistory.value.length - 1]
        }
    }

    function reset() {
        cursor.value = null
        cursorHistory.value = [null]
    }

    function setPageSize(n: number) {
        pageSize.value = n
        reset()
    }

    function applyResult(result: {
        pageInfo: PageInfo
        totalCount?: number | null
        itemCount?: number
    }) {
        pageInfo.value = result.pageInfo
        if (result.totalCount != null) totalCount.value = result.totalCount
        if (result.itemCount != null) lastPageItemCount.value = result.itemCount
    }

    return {
        pageSize,
        cursor,
        pageInfo,
        totalCount,
        currentPageIndex,
        canGoPrev,
        canGoNext,
        rangeStart,
        rangeEnd,
        nextPage,
        prevPage,
        reset,
        setPageSize,
        applyResult
    }
}
